import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userIdentityQuery, userFreeTrialStatusQuery } from '../graphql-operations/UserIdentity.graphql';
import { isBundleCartItem } from '../lib/cart';
import * as types from '../types';
import { userIdentity } from '../types/gql-op-types';

export type FreeTrialRestrictionReason = 'in_cart' | 'previously_purchased' | null;

interface FreeTrialEligibility {
  isEligible: boolean;
  restrictionReason: FreeTrialRestrictionReason;
}

interface UserFreeTrialStatus {
  currentUser?: {
    userHouseholds?: Array<{
      household?: {
        pets?: Array<{
          device?: {
            subscription?: {
              hasFreeTrialPlan?: boolean | null;
            } | null;
          } | null;
        }>;
      };
    }>;
  };
}

/**
 * Checks if user is eligible for a free trial.
 * Returns ineligible if user has a free trial in cart or has an existing free trial subscription.
 */
export default function useFreeTrialEligibility(collarProductId: string): FreeTrialEligibility {
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);

  const { data: userData } = useQuery<userIdentity>(userIdentityQuery, {
    skip: !collarProductId,
  });

  const previouslyPurchasedCollarProductIds = useMemo(
    () => userData?.currentUser?.previouslyPurchasedCollarProductIds ?? [],
    [userData?.currentUser?.previouslyPurchasedCollarProductIds],
  );

  const { data: freeTrialData } = useQuery<UserFreeTrialStatus>(userFreeTrialStatusQuery, {
    skip: !userData?.currentUser,
  });

  const hasExistingFreeTrial = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.subscription?.hasFreeTrialPlan === true));
  }, [freeTrialData]);

  return useMemo(() => {
    // Check if user has previously purchased this product type
    if (previouslyPurchasedCollarProductIds.includes(collarProductId)) {
      return {
        isEligible: false,
        restrictionReason: 'previously_purchased',
      };
    }

    // Check if logged-in user has an active free trial subscription on any product
    if (hasExistingFreeTrial) {
      return {
        isEligible: false,
        restrictionReason: 'previously_purchased',
      };
    }

    // Check if cart contains any free trial
    const cartItems = Object.values(cart.cartItems);
    const hasFreeTrialInCart = cartItems.some((item) => {
      if (!isBundleCartItem(item)) {
        return false;
      }

      const subscriptionProduct = products.find(
        (p): p is types.ISubscriptionProduct =>
          types.isModuleSubscriptionProduct(p) && p.sku === item.subscriptionLineItem.sku,
      );

      return subscriptionProduct?.freeTrial === true;
    });

    if (hasFreeTrialInCart) {
      return {
        isEligible: false,
        restrictionReason: 'in_cart',
      };
    }

    return {
      isEligible: true,
      restrictionReason: null,
    };
  }, [cart.cartItems, products, previouslyPurchasedCollarProductIds, collarProductId, hasExistingFreeTrial]);
}
